import { BIS } from 'data/dataSourceConstants';

const RESOLVED_FIRST_DOC_STATUSES = new Set([
	'SIGNOFF',
	'COMPLETED',
	'SIGNED-OFF',
	'WITHDRAWN',
	'SUSPENDED',
]);

const OPEN_FIRST_DOC_STATUSES = new Set([
	'A/P UNPAID',
	'A/P TO D.E.A.R',
	'A/P ENTIRE',
	'AP-NPE',
	'ASSIGNED TO P/E',
	'P/E IN PROCESS',
	'P/E DISAPPROVED',
	'P/E PARTIAL APRV',
	'P/E PAA $ PENDING',
	'P/E PAA $ RESOLVD',
	'APPROVED',
	'PERMIT-PARTIAL',
	'PERMIT-ENTIRE',
]);

const LAST_DOC_CLOSE_WARNING_STATUS = 'P APPROVED';

const STATUS_ALIASES = {
	'PERMIT ENTIRE': 'PERMIT-ENTIRE',
	'PERMIT - ENTIRE': 'PERMIT-ENTIRE',
	'PERMIT - PARTIAL': 'PERMIT-PARTIAL',
	'X SIGNED OFF': 'SIGNED-OFF',
	'U COMPLETED': 'COMPLETED',
	'W WITHDRAWN': 'WITHDRAWN',
	'V SUSPENDED': 'SUSPENDED',
	'SIGNED OFF': 'SIGNED-OFF',
	'SIGN-OFF (ARA)': 'SIGNOFF',
	'LOC ISSUED': 'SIGNOFF',
	'H P/E IN PROCESS': 'P/E IN PROCESS',
	'Q PERMIT-PARTIAL': 'PERMIT-PARTIAL',
	'J P/E DISAPPROVED': 'P/E DISAPPROVED',
	'K P/E PARTIAL APRV': 'P/E PARTIAL APRV',
	'G PAA FEE DUE': 'P/E PAA $ PENDING',
	'L P/E PAA $ PENDIN': 'P/E PAA $ PENDING',
	'L P/E PAA $ PENDING': 'P/E PAA $ PENDING',
	'M P/E PAA $ RESOLV': 'P/E PAA $ RESOLVD',
	'F ASSIGNED TO P/E': 'ASSIGNED TO P/E',
	'B A/P UNPAID': 'A/P UNPAID',
	'A PRE-FILED': 'PRE-FILED',
	'E AP-NPE': 'AP-NPE',
	'D A/P ENTIRE': 'A/P ENTIRE',
	'C A/P TO D.E.A.R': 'A/P TO D.E.A.R',
	'R PERMIT ENTIRE': 'PERMIT-ENTIRE',
	'P APPROVED': LAST_DOC_CLOSE_WARNING_STATUS,
};

export function getJobNumber(row, dataSource) {
	return dataSource === BIS ? row.job_number : row.job__;
}

export function getDocNumber(row, dataSource) {
	return dataSource === BIS ? row.doc_number : row.doc__;
}

export function getRowStatus(row, dataSource) {
	return dataSource === BIS ? row.job_status : row.job_status_descrp;
}

// A row's own job_status can lag BIS's actual outcome: a job the detail page's
// "JOB WITHDRAWN AS OF <date>" banner confirms withdrawn sometimes still lists an
// old open-looking job_status on the list page (e.g. stuck at "Q PERMIT-PARTIAL"
// for years). withdrawn_date is scraped straight off that banner (BIS source
// only), so its presence is treated as authoritative over job_status here.
// Mirrors BisJobApplicationGroupCounter::effectiveStatus() on the API side.
export function getEffectiveRowStatus(row, dataSource) {
	if (dataSource === BIS && row.withdrawn_date) {
		return 'WITHDRAWN';
	}
	return getRowStatus(row, dataSource);
}

export function parseDocNumber(docNumber) {
	if (docNumber === null || docNumber === undefined || docNumber === '') {
		return 0;
	}
	const parsed = parseInt(String(docNumber).replace(/\D/g, ''), 10);
	return Number.isNaN(parsed) ? 0 : parsed;
}

export function normalizeJobApplicationStatus(rawStatus, dataSource) {
	if (rawStatus === null || rawStatus === undefined) {
		return '';
	}

	let status = String(rawStatus).trim().toUpperCase();
	if (!status) {
		return '';
	}

	if (STATUS_ALIASES[status]) {
		return STATUS_ALIASES[status];
	}

	if (dataSource === BIS) {
		// DOB ships both letter-coded and digit-coded prefixes for the same status,
		// for example 'V SUSPENDED' and '3 SUSPENDED', which must normalize
		// identically. Keep this character class in step with
		// BisJobApplicationGroupCounter::STATUS_PREFIX_PATTERN on the API side: the
		// sidebar badge is computed there and this table's tab counts are computed
		// here, so a prefix stripped on one side only makes the two disagree.
		const withoutPrefix = status.replace(/^[A-Z0-9]\s+/, '');
		if (STATUS_ALIASES[withoutPrefix]) {
			return STATUS_ALIASES[withoutPrefix];
		}
		if (STATUS_ALIASES[status]) {
			return STATUS_ALIASES[status];
		}
		return withoutPrefix;
	}

	return status;
}

export function getDisplayStatus(rawStatus, dataSource) {
	const normalized = normalizeJobApplicationStatus(rawStatus, dataSource);
	return normalized || rawStatus || '-';
}

export function isPApprovedStatus(rawStatus, dataSource) {
	const normalized = normalizeJobApplicationStatus(rawStatus, dataSource);
	return normalized === LAST_DOC_CLOSE_WARNING_STATUS;
}

export function isCloseWarningFirstDocStatus(rawStatus, dataSource) {
	const normalized = normalizeJobApplicationStatus(rawStatus, dataSource);
	return OPEN_FIRST_DOC_STATUSES.has(normalized);
}

export function getApplicationStatus(firstDocStatus, dataSource) {
	const normalizedFirst = normalizeJobApplicationStatus(firstDocStatus, dataSource);

	return RESOLVED_FIRST_DOC_STATUSES.has(normalizedFirst) ? 'resolved' : 'open';
}

export function needsRequestToClose(firstDocStatus, lastDocStatus, docCount, dataSource) {
	if (docCount < 2) {
		return false;
	}

	return (
		isCloseWarningFirstDocStatus(firstDocStatus, dataSource) &&
		isPApprovedStatus(lastDocStatus, dataSource)
	);
}

export function groupJobApplications(rows, dataSource) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return [];
	}

	const groupsByJob = new Map();

	rows.forEach((row) => {
		const jobNumber = getJobNumber(row, dataSource);
		if (!jobNumber) {
			return;
		}

		if (!groupsByJob.has(jobNumber)) {
			groupsByJob.set(jobNumber, []);
		}
		groupsByJob.get(jobNumber).push(row);
	});

	const groups = Array.from(groupsByJob.entries()).map(([jobNumber, documents]) => {
		const sortedDocs = [...documents].sort(
			(a, b) => parseDocNumber(getDocNumber(a, dataSource)) - parseDocNumber(getDocNumber(b, dataSource))
		);
		const totalDocs = sortedDocs.length;
		const firstDoc = sortedDocs[0];
		const lastDoc = sortedDocs[totalDocs - 1];
		const firstDocStatus = getRowStatus(firstDoc, dataSource);
		const lastDocStatus = getRowStatus(lastDoc, dataSource);
		const applicationStatus = getApplicationStatus(getEffectiveRowStatus(firstDoc, dataSource), dataSource);

		return {
			jobNumber,
			applicationStatus,
			needsCloseWarning: needsRequestToClose(
				firstDocStatus,
				lastDocStatus,
				totalDocs,
				dataSource
			),
			documents: sortedDocs.map((doc, index) => ({
				...doc,
				docLabel: `Doc (${index + 1} of ${totalDocs})`,
				displayStatus: getDisplayStatus(getRowStatus(doc, dataSource), dataSource),
			})),
		};
	});

	return groups.sort((a, b) => String(a.jobNumber).localeCompare(String(b.jobNumber)));
}

export function filterJobApplicationGroups(groups, filteredStatus) {
	if (!filteredStatus || filteredStatus === 'all') {
		return groups;
	}

	if (filteredStatus === 'open') {
		return groups.filter((group) => group.applicationStatus === 'open');
	}

	if (filteredStatus === 'closed') {
		return groups.filter((group) => group.applicationStatus === 'resolved');
	}

	return groups;
}

export function countJobApplicationGroups(groups) {
	const numOpen = groups.filter((g) => g.applicationStatus === 'open').length;
	const numClosed = groups.filter((g) => g.applicationStatus === 'resolved').length;
	return {
		numOpen,
		numClosed,
		numCombined: groups.length,
	};
}

export function countOpenJobApplications(rows, dataSource) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return 0;
	}
	return countJobApplicationGroups(groupJobApplications(rows, dataSource)).numOpen;
}

export function paginateGroups(groups, page, perPage) {
	const totalPages = Math.max(1, Math.ceil(groups.length / perPage));
	const safePage = Math.min(Math.max(page, 1), totalPages);
	const start = (safePage - 1) * perPage;

	return {
		groups: groups.slice(start, start + perPage),
		currentPage: safePage,
		totalPages,
	};
}
